import React from "react";
import { Link } from "react-router-dom";
import { HelpCircle, Package, Barcode, ScanLine, Users, Store, CreditCard, Settings, Mail, ChevronDown, BookOpen } from "lucide-react";

/**
 * In-app Help / Docs hub. Lives at /help and is accessible from the App footer.
 * Sections are collapsible — first one open by default. Add new sections by
 * appending to the SECTIONS array below.
 */

const SECTIONS = [
  {
    id: "getting-started",
    icon: BookOpen,
    title: "Getting started in 5 minutes",
    body: [
      "1. Add your first product from /products → New product. Give it a name, a base SKU prefix (e.g. 'TEE'), and at least one variant. Vendor Vault auto-generates a unique SKU and Code128 barcode for every variant.",
      "2. Print booth tags from the product detail page. The 'Print Tag' button opens a printable shipping-tag layout sized for both inkjet sticker paper and standard tag stock.",
      "3. Create your first event (a show / con / market) from /events. Set the dates and an optional tax rate. Tax is applied only on receipts — never on the underlying sales total.",
      "4. Open /scanner on your phone (after installing Vendor Vault as a PWA — see 'Install on phone' below). Use the webcam to scan a tag or type the SKU. The product + variant comes up; tap 'Log sale' and you're done.",
      "5. Review the day from the event detail page — the sale-by-sale ledger, by-day chart, and per-seller payout breakdown are all there.",
    ],
  },
  {
    id: "barcodes",
    icon: Barcode,
    title: "Barcodes & tags",
    body: [
      "Every variant gets a unique 12-digit numeric Code128 barcode plus a SKU formatted as {BASE_SKU}-{NNN}. These are auto-generated when you create a variant and are unique across your entire shop.",
      "The Print Tag layout includes the product name, variant attributes, price, SKU, and the scannable barcode. Print on regular sticker paper or shipping-tag stock with a standard inkjet/laser printer.",
      "Need to re-print? Open the product, click 'Print Tag' — it'll print every variant in a column ready to cut.",
      "USB and Bluetooth barcode scanners work too: focus the scanner input on /scanner and the scanner types the code straight into the field.",
    ],
  },
  {
    id: "scanner",
    icon: ScanLine,
    title: "Scanner & sales",
    body: [
      "On /scanner, the webcam preview searches for any Code128 / EAN / UPC. As soon as it locks a match in your catalog, the product card appears with a 'Log sale' button.",
      "Cart bar (top of the page): every successful sale gets added to a running cart for that event. Expand to see itemized lines with per-item remove (which restocks the variant). The cart persists in localStorage and survives Square POS round-trips and tab kills.",
      "Custom amount: tap the '$ Custom' button to add a one-off sale without a product (booth deposits, custom commissions, etc.) — no stock movement.",
      "Charge with Square: if your shop has the Square POS integration connected, the Scanner shows a 'Charge with Square' button that deep-links into the native Square app with the cart total pre-filled.",
    ],
  },
  {
    id: "team",
    icon: Users,
    title: "Inviting your team",
    body: [
      "From /team (admin only), enter a teammate's email to invite them. They'll get an email from Resend with the sign-in link. The first time they sign in with that email, they're attached to your shop as Staff.",
      "Staff can: view products, run the scanner, log sales, adjust stock.",
      "Staff cannot: create/delete products, manage events, see revenue or per-seller payouts, manage billing, manage the team.",
      "Plan limits: Free = solo (no team). Pro = 5 team members. Business = unlimited.",
    ],
  },
  {
    id: "shopify",
    icon: Store,
    title: "Connecting Shopify",
    body: [
      "Pro & Business only. Visit /billing and find the 'Shopify' card. Enter your myshopify.com store domain and click 'Connect Shopify' — you'll be redirected to Shopify to approve the install.",
      "After connecting, the 'Sync all' button pushes your Vendor Vault catalog (products + variants + stock + categories as Custom Collections) to Shopify. Use 'Test sync (1 product)' first to verify the connection.",
      "Resume: if a sync gets interrupted mid-run (e.g. the worker recycles), a 'Resume sync' button appears to finish only the products that weren't pushed.",
      "Reset mapping: if you delete products on the Shopify side and want a fresh sync, use 'Reset mapping' to clear the stored Shopify-product-id links.",
    ],
  },
  {
    id: "square",
    icon: CreditCard,
    title: "Connecting Square POS",
    body: [
      "Pro & Business only. From /billing → Square POS card, click 'Connect Square'. You'll be redirected to Square to approve the install and pick the seller account you want connected.",
      "After connecting, the Scanner cart shows a 'Charge with Square' button. Tapping it deep-links into the native Square POS app on your phone (Android or iOS) with the cart total pre-filled. Your existing Square Reader (or Tap-to-Pay) handles the card.",
      "When the payment completes, Square POS bounces you back to Vendor Vault with the payment confirmed, and the cart is automatically cleared and stamped with the Square payment ID.",
      "If the Square POS app isn't installed when you tap, you'll see an 'Install Square POS' link to the right App Store / Play Store.",
    ],
  },
  {
    id: "offline",
    icon: Package,
    title: "Offline mode & PWA",
    body: [
      "Install Vendor Vault as a PWA — on iOS: open Safari, tap Share → 'Add to Home Screen'. On Android: open Chrome, tap the menu → 'Install app'. The app launches full-screen, with its own icon.",
      "Offline reads: products, scanner lookups, and event detail pages keep working from a local IndexedDB cache the moment the network drops.",
      "Offline writes: sales and stock adjustments made offline get queued locally with an idempotency key. When the connection returns, they auto-sync to the server.",
      "The navbar pill shows 'Online' / 'Offline · N queued' / 'Online · N queued (syncing)' so you always know the state.",
    ],
  },
  {
    id: "billing",
    icon: Settings,
    title: "Billing, trial & cancellation",
    body: [
      "Every new account starts with a 14-day Pro trial. No credit card needed up front — Pro features are unlocked for that window so you can run a real show on a real plan.",
      "Upgrade: /billing → pick a plan → Stripe Checkout. We use Stripe for all payments; we never see or store your card number.",
      "Manage / cancel: /billing → 'Manage subscription' opens the Stripe Customer Portal where you can swap plans, switch monthly/yearly, update card, or cancel.",
      "Cancellation keeps your access until the end of the period you've paid for. After that, your shop drops back to Free — your data stays accessible (within the 20-product / 1-event limits) and can be CSV-exported anytime.",
    ],
  },
];

export default function Help() {
  const [openId, setOpenId] = React.useState(SECTIONS[0].id);

  return (
    <div className="space-y-6">
      <div>
        <span className="brutal-badge">Help</span>
        <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight mt-3">
          Help &amp; docs
        </h1>
        <p className="mt-3 font-mono text-sm text-[#4A4A4A] max-w-2xl leading-relaxed">
          Everything you need to run Vendor Vault at your next show. Looking for the marketing-level FAQ?{" "}
          <Link to="/" className="text-[#C2410C] underline">It's on the landing page.</Link>
        </p>
      </div>

      {/* Quick-jump grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="help-quickjump">
        {SECTIONS.map((s) => (
          <a
            key={s.id}
            href={`#${s.id}`}
            onClick={(e) => { e.preventDefault(); setOpenId(s.id); document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
            className="brutal-card-sm bg-white p-3 hover:bg-[#F4F2EC] flex items-center gap-2"
            data-testid={`help-jump-${s.id}`}
          >
            <s.icon size={16} className="text-[#C2410C] flex-shrink-0"/>
            <span className="font-mono text-xs uppercase tracking-widest leading-tight">{s.title}</span>
          </a>
        ))}
      </div>

      <div className="space-y-3" data-testid="help-sections">
        {SECTIONS.map((s) => {
          const open = openId === s.id;
          const Icon = s.icon;
          return (
            <div
              key={s.id}
              id={s.id}
              className={`brutal-card-sm bg-white overflow-hidden scroll-mt-24 ${open ? "shadow-[3px_3px_0_0_#111]" : ""}`}
            >
              <button
                type="button"
                onClick={() => setOpenId(open ? "" : s.id)}
                className="w-full px-5 py-4 flex items-start justify-between gap-4 text-left hover:bg-[#F4F2EC]"
                data-testid={`help-toggle-${s.id}`}
                aria-expanded={open}
              >
                <span className="flex items-center gap-3">
                  <span className={`w-8 h-8 border-2 border-black flex items-center justify-center flex-shrink-0 ${open ? "bg-[#C2410C]" : "bg-[#F4F2EC]"}`}>
                    <Icon size={16} className={open ? "text-white" : ""}/>
                  </span>
                  <span className="font-heading text-base lg:text-lg font-black uppercase leading-snug">{s.title}</span>
                </span>
                <ChevronDown size={20} className={`flex-shrink-0 mt-2 transition-transform ${open ? "rotate-180" : ""}`}/>
              </button>
              {open && (
                <div className="px-5 pb-5 -mt-1 space-y-3 font-mono text-sm leading-relaxed text-[#333]" data-testid={`help-body-${s.id}`}>
                  {s.body.map((p, i) => <p key={i}>{p}</p>)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="brutal-card bg-[#111] text-white p-6 lg:p-8 mt-10">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <Mail size={22} className="text-[#C2410C]"/>
              <h3 className="font-heading text-2xl font-black uppercase">Still stuck?</h3>
            </div>
            <p className="font-mono text-sm opacity-80 mt-2 max-w-xl">
              Email <a href="mailto:support@vendorvaultonline.com" className="text-[#C2410C] underline">support@vendorvaultonline.com</a> — we're a small team and we read everything. Include a screenshot if it's a visual bug.
            </p>
          </div>
          <a
            href="mailto:support@vendorvaultonline.com"
            className="brutal-btn whitespace-nowrap"
            data-testid="help-email-cta"
          >
            <HelpCircle size={16}/> Contact support
          </a>
        </div>
      </div>
    </div>
  );
}
